const compactValue = value => value !== undefined && value !== null;
const valueFor = (source, ...keys) => {
  if (!source) return undefined;

  return keys.reduce(
    (current, key) => (current === undefined ? source[key] : current),
    undefined
  );
};

const normalizeReportItem = item => {
  if (!item || !item.key) return null;

  return Object.fromEntries(
    Object.entries({
      key: item.key,
      label: item.label,
      before: item.before,
      after: item.after,
      value: item.value,
      status: item.status,
      changed: item.changed,
      notes: item.notes,
    }).filter(([, value]) => compactValue(value))
  );
};

export const normalizeReportItems = items =>
  (Array.isArray(items) ? items : []).map(normalizeReportItem).filter(Boolean);

export const alteredReportCategories = ({
  amenities = [],
  installations = [],
} = {}) =>
  [...normalizeReportItems(amenities), ...normalizeReportItems(installations)]
    .filter(item => {
      if (item.changed === true) return true;
      if (item.status && item.status !== 'unchanged') return true;
      if ('before' in item && 'after' in item) {
        return String(item.before) !== String(item.after);
      }
      return compactValue(item.value) && item.value !== '';
    })
    .map(item => item.key);

export const buildPostVisitReportPayload = ({
  amenities = [],
  installations = [],
  listingReport = {},
  artifactIdempotencyKey = '',
  followUp = null,
  origin = 'dashboard',
  photos = null,
  reason = null,
  reviewContext = null,
  status = 'realizada',
} = {}) => {
  const normalizedAmenities = normalizeReportItems(amenities);
  const normalizedInstallations = normalizeReportItems(installations);
  const alteredCategories = alteredReportCategories({
    amenities: normalizedAmenities,
    installations: normalizedInstallations,
  });

  return Object.fromEntries(
    Object.entries({
      artifact_idempotency_key: artifactIdempotencyKey || undefined,
      amenities: normalizedAmenities,
      installations: normalizedInstallations,
      listing_report: listingReport || {},
      follow_up: followUp,
      origin,
      photos: Array.isArray(photos) && photos.length ? photos : undefined,
      reason,
      review_context: reviewContext,
      status,
      categorias_alteradas_count: alteredCategories.length,
      categorias_alteradas: alteredCategories,
    }).filter(([, value]) => compactValue(value))
  );
};

export const createPostVisitArtifactRevisionId = () => {
  const randomUUID =
    typeof window !== 'undefined' && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `post-visit-report:${randomUUID}`;
};

export const postVisitArtifactFrom = visit =>
  valueFor(visit, 'postVisitArtifact', 'post_visit_artifact') || {};

export const postVisitReportVersions = artifact =>
  valueFor(artifact, 'reportVersions', 'report_versions') || [];

export const latestPostVisitReportVersion = artifact => {
  const versions = postVisitReportVersions(artifact);
  return (
    valueFor(artifact, 'latestReportVersion', 'latest_report_version') ||
    versions[versions.length - 1] ||
    null
  );
};

export const postVisitReportDiff = version => valueFor(version, 'diff') || [];

export const postVisitArtifactState = visit => {
  if (!visit?.id) return 'loading';

  const artifact = postVisitArtifactFrom(visit);
  const latestVersion = latestPostVisitReportVersion(artifact);
  if (!latestVersion) return 'empty';

  if (['nao_compareceu', 'no_show'].includes(visit.status)) return 'no_show';
  if (['nao_sucedida', 'not_done'].includes(visit.status)) return 'not_done';
  if (visit.status === 'realizada') return 'done';

  return 'review';
};

export const postVisitVersionLabel = version => {
  if (!version) return 'Sem versao';

  const versionNumber =
    valueFor(version, 'versionNumber', 'version_number') || 'nova';
  const source = valueFor(version, 'source') || 'api';

  return `Versao ${versionNumber} - ${source}`;
};

export const postVisitDiffRows = version =>
  postVisitReportDiff(version).map(item => ({
    path: valueFor(item, 'path') || 'payload',
    before: compactValue(valueFor(item, 'before'))
      ? valueFor(item, 'before')
      : '—',
    after: compactValue(valueFor(item, 'after'))
      ? valueFor(item, 'after')
      : '—',
  }));
